import { textVariant } from '@/config'
import { PropsOf } from '@codeleap/types'
import { Attachment } from 'stream-chat'
import { createStyles } from '@codeleap/styles'
import { useConditionalMessageStyle } from '../hooks'
import { View, Text, Icon, Touchable, ActivityIndicator } from '@/components'
import { getFileSizeDisplayText } from '../utils'
import { useCallback } from 'react'
import useDownloader from 'react-use-downloader'

export type MessageAttachmentProps = PropsOf<typeof Touchable> & {
  attachment: Attachment
}

export const MessageAttachment = ({
  attachment,
  ...rest
}: MessageAttachmentProps) => {
  const { styles } = useStyles()

  const { download, isInProgress } = useDownloader()

  const handleDownload = useCallback(() => {
    return download(attachment?.asset_url, attachment?.title || attachment?.text || attachment?.author_name)
  }, [attachment, download])

  const mime = typeof attachment.mime_type === 'string'
    ? attachment.mime_type.split('/').pop()
    : ''

  const isDocument = attachment.type === 'file'

  return (
    <Touchable {...rest} onPress={handleDownload} style={[styles.touchable]}>
      <View style={styles.wrapper}>
        <View style={styles.iconWrapper}>
          {isInProgress ? (
            <ActivityIndicator style={['tiny']} />
          ) : (
            <Icon name='download-02' style={['size:2', styles.icon]} />
          )}
        </View>

        <View style={styles.contentWrapper}>
          <Text
            style={styles.title}
            text={attachment.title || attachment?.fallback}
          />
          <Text
            text={`${mime} ${isDocument ? 'DOCUMENT' : 'FILE'} - ${getFileSizeDisplayText(attachment.file_size)}`.toUpperCase()}
            style={styles.description}
          />
        </View>
      </View>
    </Touchable>
  )
}

const useStyles = () => {
  const { styles } = useConditionalMessageStyle(baseStyle, myMessageStyle)

  return { styles }
}

const baseStyle = createStyles(theme => ({
  touchable: {},
  wrapper: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    ...theme.spacing.padding(1),
    borderRadius: theme.radius.small,
    backgroundColor: theme.baseColors.primarySolid200,
    ...theme.presets.fullWidth,

    [theme.media.down('tabletSmall')]: {
      maxWidth: '70vw',
    },
  },
  contentWrapper: {
    ...theme.presets.column,
    ...theme.spacing.gap(0.5),
    flex: 1,
    minWidth: 0,
  },
  title: {
    ...textVariant(['p3']),
    color: theme.baseColors.onPrimarySolid200Heading,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  description: {
    ...textVariant(['c2']),
    whiteSpace: 'nowrap',
    color: theme.baseColors.onPrimarySolid200Caption,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  iconWrapper: {
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    maxWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxHeight: theme.size.largeish,
    ...theme.presets.center,
    borderRadius: theme.radius.infinite,
    backgroundColor: theme.baseColors.primarySolid50,
    marginRight: theme.spacing.value(1.5),
  },
  icon: {
    color: theme.baseColors.onPrimarySolid50Caption,
  },
}))

const myMessageStyle = createStyles(theme => ({
  touchable: {},
  wrapper: {
    backgroundColor: theme.baseColors.primarySolid300,
  },
  title: {
    color: theme.baseColors.onPrimarySolid300Heading,
  },
  description: {
    color: theme.baseColors.onPrimarySolid300Caption,
  },
  iconWrapper: {
    backgroundColor: theme.baseColors.primarySolid500,
  },
  icon: {
    color: theme.baseColors.onPrimarySolid500Heading,
  },
}))
